// @ts-check

/**
 * @file Renders the uploaded file detail route.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Image, Linking, Platform, View} from "react-native"
import Button from "../../../src/components/button"
import buildRoutePath from "../../../src/shared/build-route-path"
import Card from "../../../src/components/card"
import DetailValue from "../../../src/components/detail-value"
import errorMessage from "../../../src/shared/error-message"
import {requiredRouteParam} from "../../../src/shared/route-params"
import ScreenTitle from "../../../src/components/screen-title"
import Text from "@kaspernj/api-maker/build/utils/text"
import {UploadedFile} from "../../../src/frontend-models/uploaded-file"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"

/** @import {BoardThemeColors} from "../../../src/shared/theme" */
/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * Checks whether a file name has an image extension.
 * @param {string | null | undefined} fileName File name to inspect.
 * @returns {boolean} True when the file name is an image.
 */
function isImageFile(fileName) {
  if (!fileName) return false
  const lower = fileName.toLowerCase()
  return [".bmp", ".gif", ".jpeg", ".jpg", ".png", ".webp"].some((ext) =>
    lower.endsWith(ext)
  )
}

/** @typedef {{[key: string]: unknown}} UploadedFilesShowScreenProps */

/**
 * @typedef {object} UploadedFilesShowScreenState
 * @property {string | null} fileObjectUrl Browser URL for the downloaded file.
 * @property {boolean} loading Whether the file details are loading.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 * @property {UploadedFile | null} uploadedFile Loaded uploaded file record.
 */

/**
 * Uploaded file details screen using frontend models.
 * @augments {ShapeComponent<UploadedFilesShowScreenProps, UploadedFilesShowScreenState>}
 */
class UploadedFilesShowScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {string} */
  uploadedFileID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {UploadedFilesShowScreenState} */
  state = {
    fileObjectUrl: /** @type {string | null} */ null,
    loading: true,
    status: /** @type {{kind: "error", text: string} | null} */ null,
    uploadedFile: /** @type {UploadedFile | null} */ null
  }

  /** Initializes route params and uploaded file loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()

    const uploadedFileID = requiredRouteParam(params, "id", "Uploaded file ID")
    this.params = params
    this.uploadedFileID = uploadedFileID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      /** @type {string | null} */
      let createdObjectUrl = null
      const loadData = async () => {
        try {
          const uploadedFile = await UploadedFile
            .preload({
              user: true
            })
            .find(uploadedFileID)
          if (cancelled) return
          this.s.loading = false
          this.s.uploadedFile = uploadedFile
          const fileObjectUrl = await loadFileObjectUrl(uploadedFile)
          if (cancelled) {
            if (fileObjectUrl && typeof window !== "undefined") {
              window.URL.revokeObjectURL(fileObjectUrl)
            }
            return
          }
          createdObjectUrl = fileObjectUrl
          this.s.fileObjectUrl = fileObjectUrl
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
          throw error
        }
      }
      if (uploadedFileID) {
        void loadData()
      } else {
        this.s.loading = false
        this.s.status = {
          kind: "error",
          text: _("File not found")
        }
      }
      return () => {
        cancelled = true
        if (createdObjectUrl && typeof window !== "undefined") {
          window.URL.revokeObjectURL(createdObjectUrl)
        }
      }
    }, [uploadedFileID])
  }

  /**
   * Renders uploaded file details.
   * @returns {ReactNode} Uploaded file detail content.
   */
  render() {
    const {loading, status, uploadedFile} = this.s
    const {params, uploadedFileID} = this.tt
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const screenTitle = uploadedFile
      ? uploadedFile.title() || String(uploadedFile.id())
      : _("Uploaded file")
    let uploadedBy = "-"

    if (uploadedFile) {
      const user = uploadedFile.user()

      if (user) {
        const fullName = [user.firstName(), user.lastName()]
          .filter(Boolean)
          .join(" ")
          .trim()

        uploadedBy = fullName || user.email() || "-"
      }
    }

    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="uploadedFileShowScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="uploadedFileShowScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <View
          testID="uploadedFileShowScreen/content"
        >
          {status &&
            <Text
              style={styles[`statusError-${themeName}`] ||= {
                color: colors.dangerText,
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="uploadedFileShowScreen/status"
            >
              {status.text}
            </Text>
          }
          {uploadedFile &&
            <Card
              style={styles.cardSpacing ||= {padding: 20}}
              testID="uploadedFileShowScreen/card"
            >
              <View
                style={styles.headerRow ||= {
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 20
                }}
                testID="uploadedFileShowScreen/headerRow"
              >
                <Text
                  style={styles.headerTitle ||= {
                    fontSize: 24,
                    fontWeight: 700
                  }}
                  testID="uploadedFileShowScreen/title"
                >
                  {screenTitle}
                </Text>
                <Button
                  href={buildRoutePath(params, `/uploaded-files/${uploadedFileID}/edit`)}
                  iconName="pen-to-square"
                  label={_("Edit")}
                  testID="uploadedFileShowScreen/editButton"
                />
              </View>
              <Text
                style={styles.sectionTitle ||= {
                  fontSize: 18,
                  fontWeight: 700,
                  marginBottom: 12
                }}
                testID="uploadedFileShowScreen/detailsTitle"
              >
                {_("Details")}
              </Text>
              {detailRow({
                colors,
                label: "Id",
                testID: "id",
                themeName,
                value: String(uploadedFile.id())
              })}
              {detailRow({
                colors,
                label: _("Title"),
                testID: "titleValue",
                themeName,
                value: uploadedFile.title() || String(uploadedFile.id())
              })}
              {detailRow({
                colors,
                label: _("Uploaded by"),
                testID: "uploadedByValue",
                themeName,
                value: uploadedBy
              })}
              {detailRow({
                colors,
                label: _("Resource"),
                testID: "resourceValue",
                themeName,
                value:
                  uploadedFile.resourceType() && uploadedFile.resourceId()
                    ? `${uploadedFile.resourceType()} #${uploadedFile.resourceId()}`
                    : "-"
              })}
              {this.fileSection()}
            </Card>
          }
        </View>
      </View>
    )
  }

  /**
   * Renders the file preview and open action.
   * @returns {ReactNode} File preview section.
   */
  fileSection() {
    const {fileObjectUrl, uploadedFile} = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const fileName = /** @type {string | null | undefined} */ (uploadedFile?.fileName())
    return (
      <View
        style={styles.fileSection ||= {
          marginTop: 16
        }}
        testID="uploadedFileShowScreen/fileSection"
      >
        <Text
          style={styles[`fileLabel-${themeName}`] ||= {
            color: colors.textMuted,
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 8
          }}
          testID="uploadedFileShowScreen/fileLabel"
        >
          {_("File")}
        </Text>
        {fileObjectUrl && isImageFile(fileName) &&
          <Image
            source={
              styles[`fileSource-${fileObjectUrl}`] ||= {
                uri: fileObjectUrl
              }
            }
            style={styles.filePreview ||= {
              borderRadius: 8,
              height: 240,
              maxWidth: 360,
              width: "100%"
            }}
            testID="uploadedFileShowScreen/filePreview"
          />
        }
        {fileObjectUrl &&
          <View
            style={styles[`openFileSpacing-${isImageFile(fileName)}`] ||= {
              alignSelf: "flex-start",
              marginTop: isImageFile(fileName) ? 12 : 0
            }}
          >
            <Button
              iconName="arrow-up-right-from-square"
              label={_("Open file")}
              onPress={() => openUrl(fileObjectUrl)}
              testID="uploadedFileShowScreen/openFileButton"
              variant="neutral"
            />
          </View>
        }
      </View>
    )
  }
}

/** @type {NamedExoticComponent<UploadedFilesShowScreenProps>} */
const UploadedFilesShowScreenComponent = memo(shapeComponent(UploadedFilesShowScreen))

export default UploadedFilesShowScreenComponent

/**
 * Calls UploadedFile#downloadFile to fetch the file bytes from Velocious
 * as base64, then turns them into an Object URL the browser can use as a
 * regular image source or window.open target. Falls back to a data URL
 * outside the browser.
 * @param {UploadedFile} uploadedFile Uploaded file to download.
 * @returns {Promise<string | null>} Browser object URL, data URL, or null.
 */
async function loadFileObjectUrl(uploadedFile) {
  const payload = await uploadedFile.downloadFile()
  const contentBase64 = payload.contentBase64
  if (!contentBase64) return null
  const contentType = payload.contentType
  if (typeof window !== "undefined" && typeof window.atob === "function") {
    const binary = window.atob(contentBase64)
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0)
    )
    const blob = new window.Blob([bytes], {
      type: contentType
    })
    return window.URL.createObjectURL(blob)
  }
  return `data:${contentType};base64,${contentBase64}`
}

/**
 * Renders one uploaded file detail row.
 * @param {{colors: BoardThemeColors, label: string, testID: string, themeName: string, value: string}} args Detail row values.
 * @returns {ReactNode} Rendered metadata row.
 */
function detailRow({colors, label, testID, themeName, value}) {
  return (
    <View
      collapsable={false}
      style={styles[`detailRow-${themeName}`] ||= {
        borderBottomColor: colors.divider,
        borderBottomWidth: 1,
        paddingVertical: 10
      }}
      testID={`uploadedFileShowScreen/${testID}/row`}
    >
      <Text
        style={styles[`detailLabel-${themeName}`] ||= {
          color: colors.textMuted,
          fontSize: 14,
          fontWeight: 600,
          marginBottom: 4
        }}
        testID={`uploadedFileShowScreen/${testID}/label`}
      >
        {label}
      </Text>
      <DetailValue
        style={styles.detailValue ||= {
          fontSize: 16
        }}
        testID={`uploadedFileShowScreen/${testID}/value`}
        value={value}
      />
    </View>
  )
}

/**
 * Opens a file URL on the current platform.
 * @param {string} url URL to open.
 * @returns {void} Nothing.
 */
function openUrl(url) {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.open(url, "_blank", "noopener")
    return
  }
  void Linking.openURL(url)
}
